import React, { useContext, useState } from "react";

import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { IMask } from "react-imask";
import { useDispatch, useSelector } from "react-redux";
import CreatableSelect from "react-select/creatable";

import RightsRestrictionsBlock from "@/src/components/contentTabs/rights/RightsRestrictionsBlock";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";
import useAbility from "@/src/hooks/useAbility";
import { getVisibleDistributionTypeList } from "@/src/utils/mediaRights";

import { createCopyright } from "../../api/legacy/ContentAPI";
import ContentContext from "../../contexts/ContentContext";
import MasterMediaContext from "../../contexts/MasterMediaContext";
import { blocksCuepoint } from "../../utils/cuepoints";
import { getMetadatas } from "../../utils/data/metadata";
import { getFreemiumEndErrorMessage } from "../../utils/freemium";
import Block from "../block/Block";
import TabsMaster from "../content/TabsMaster";
import { CHILD_CLASSIFICATIONS } from "../content/Type";
import MaskedTextFieldBase from "../form-fields/MaskedTextField";

import type { JSX } from "react";
import type { SingleValue } from "react-select";

import type { Category } from "../../api/MediaAPI.types";
import type { AppState } from "../../reducers";
import type { ContentContextValues } from "../content/ContentTabs";

// Input mask for the freemium marker: base HH:MM:SS with an optional frames
// (:FF) or milliseconds (.MS) suffix — mirrors the accepted formats above.
const FREEMIUM_MASK_BLOCKS = {
  ...blocksCuepoint,
  MS: {
    mask: IMask.MaskedRange,
    from: 0,
    to: 999,
    maxLength: 3,
  },
};

const FREEMIUM_MASK_OPTIONS = {
  mask: [
    { mask: "HH:MM:SS", blocks: FREEMIUM_MASK_BLOCKS },
    { mask: "HH:MM:SS:FF", blocks: FREEMIUM_MASK_BLOCKS },
    { mask: "HH:MM:SS.MS", blocks: FREEMIUM_MASK_BLOCKS },
  ],
};

interface CopyrightOption {
  value: string;
  label: string;
  data: Category;
}

interface CreateCopyrightResponse {
  copyrights: Category[];
}

function TabContentRightsInner(): JSX.Element {
  const { t } = useTranslation("editor-content");
  const dispatch = useDispatch();
  const token = useSelector((state: AppState) => state.loginReducer.token);
  const [freemiumEndError, setFreemiumEndError] = useState<string | undefined>(
    undefined,
  );

  const { item, isDisabled, metadatas, onItemUpdate } =
    useContext<ContentContextValues>(ContentContext);
  const { activeTab } = useContext(MasterMediaContext);
  const { isFastOnly, isOttOnly } = useAbility();
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const visibleDistributionTypeList = getVisibleDistributionTypeList({
    isFastOnly,
    isOttOnly,
    allowFast: Boolean(orgaOptions?.allow_fast),
  });

  const isMaster = item.type === "master";
  const mainMediaId = item.children.find(
    (child) => child.child_classification.key === CHILD_CLASSIFICATIONS.MAIN,
  )?.uuid;

  const isMasterFieldDisabled = isMaster && activeTab !== mainMediaId;
  const masterFieldTooltip = isMasterFieldDisabled
    ? t("masterFieldDisabledTooltip")
    : undefined;
  const isMasterFieldReadOnly = isDisabled || isMasterFieldDisabled;

  const activeMedia = isMaster
    ? item.children.find((child) => child.uuid === activeTab)
    : item;
  const freemiumEnd = activeMedia?.freemium_end ?? "";

  const addCopyright = async (data: {
    key: string;
  }): Promise<CreateCopyrightResponse> =>
    dispatch(createCopyright(data, token)) as Promise<CreateCopyrightResponse>;

  const handleNewCopyright = (newCopyrightKey: string): void => {
    addCopyright({ key: newCopyrightKey }).then((response) => {
      response.copyrights.forEach((copyright) => {
        if (copyright.key === newCopyrightKey) {
          onItemUpdate({ ...item, copyright });
        }
      });
    });
  };

  const handleCopyrightChange = (
    option: SingleValue<CopyrightOption>,
  ): void => {
    onItemUpdate({ ...item, copyright: option ? option.data : null });
  };

  const onChangeFreemiumEnd = (value: string): void => {
    if (isMaster) {
      const updatedChildren = item.children.map((child) =>
        child.uuid === activeTab ? { ...child, freemium_end: value } : child,
      );
      onItemUpdate({ ...item, children: updatedChildren });
    } else {
      onItemUpdate({ ...item, freemium_end: value });
    }

    setFreemiumEndError(undefined);
  };

  const onBlurFreemiumEnd = (): void => {
    setFreemiumEndError(
      getFreemiumEndErrorMessage(freemiumEnd, item.source_duration_ms) ??
        undefined,
    );
  };

  return (
    <div className="flex-1 space-y-8" id="rightsAnchor">
      <Block title={t("copyright")}>
        <div
          className="editor-form-input max-w-[500px]"
          data-tooltip-content={masterFieldTooltip}
          data-tooltip-id={APP_TOOLTIP_ID}
        >
          <CreatableSelect<CopyrightOption>
            closeMenuOnSelect
            isClearable
            className="editor-select"
            classNamePrefix="slct"
            isDisabled={isMasterFieldReadOnly}
            name="form-field-name"
            onChange={handleCopyrightChange}
            onCreateOption={handleNewCopyright}
            options={getMetadatas({ metadatas, type: "copyrights" })}
            placeholder={t("select")}
            value={
              item.copyright
                ? {
                    value: item.copyright.uuid,
                    label: item.copyright.key,
                    data: item.copyright,
                  }
                : null
            }
          />
        </div>
      </Block>
      <Block title={t("restrictions")}>
        <div
          data-tooltip-content={masterFieldTooltip}
          data-tooltip-id={APP_TOOLTIP_ID}
        >
          <RightsRestrictionsBlock
            disabled={isMasterFieldReadOnly}
            item={item}
            onItemUpdate={onItemUpdate}
            visibleDistributionTypeList={visibleDistributionTypeList}
          />
        </div>
      </Block>
      <Block title={t("freemium")}>
        <div className="flex max-w-[500px] flex-col">
          <div className="text-foreground flex items-center gap-1.5 py-1.25 text-[15px] font-medium">
            {t("commercialCuepoint")}
            <Info
              className="text-muted-foreground size-4"
              data-tooltip-content={t("freemiumEndFormats")}
              data-tooltip-id={APP_TOOLTIP_ID}
            />
          </div>
          <MaskedTextFieldBase
            disabled={isDisabled}
            error={freemiumEndError}
            maskOptions={FREEMIUM_MASK_OPTIONS}
            onBlur={onBlurFreemiumEnd}
            onChange={onChangeFreemiumEnd}
            placeholder="HH:MM:SS"
            value={freemiumEnd}
          />
        </div>
      </Block>
    </div>
  );
}

function TabContentRights(): JSX.Element {
  const { item } = useContext<ContentContextValues>(ContentContext);

  if (item.type === "master") {
    return (
      <TabsMaster>
        <TabContentRightsInner />
      </TabsMaster>
    );
  }

  return <TabContentRightsInner />;
}

export default TabContentRights;
